import { useEffect, useRef } from 'react'

// ── Colour per category ──────────────────────────────────────────────
const CAT: Record<string, string> = {
  target:   '#ef4444',
  identity: '#22c55e',
  social:   '#60a5fa',
  breach:   '#fb923c',
  infra:    '#facc15',
}

// ── Node definitions (ring=0 center, 1/2/3 concentric) ───────────────
const DEFS = [
  { id: 'subject',     label: 'TARGET',      cat: 'target',   ring: 0, a: 0,      ph: 0, to: ['email','phone','username','govid'] },
  // Ring 1 — primary identifiers
  { id: 'email',       label: 'Email',        cat: 'identity', ring: 1, a: -1.57,  ph: 1, to: ['facebook','breach','domain'] },
  { id: 'phone',       label: 'Phone',        cat: 'identity', ring: 1, a: 0,      ph: 1, to: ['callerid','instagram'] },
  { id: 'username',    label: 'Username',     cat: 'identity', ring: 1, a: 1.57,   ph: 1, to: ['twitter','reddit'] },
  { id: 'govid',       label: 'Gov ID',       cat: 'identity', ring: 1, a: 3.14,   ph: 1, to: ['company'] },
  // Ring 2 — derived intelligence
  { id: 'facebook',    label: 'Facebook',     cat: 'social',   ring: 2, a: -1.73,  ph: 2, to: ['location'] },
  { id: 'breach',      label: 'Breach DB',    cat: 'breach',   ring: 2, a: -0.47,  ph: 2, to: ['credentials','darkweb'] },
  { id: 'domain',      label: 'Domain',       cat: 'infra',    ring: 2, a: 0.47,   ph: 2, to: ['shodan','ip'] },
  { id: 'callerid',    label: 'Caller ID',    cat: 'identity', ring: 2, a: 1.26,   ph: 2, to: [] },
  { id: 'instagram',   label: 'Instagram',    cat: 'social',   ring: 2, a: 2.04,   ph: 2, to: [] },
  { id: 'twitter',     label: 'X / Twitter',  cat: 'social',   ring: 2, a: 3.30,   ph: 2, to: [] },
  { id: 'reddit',      label: 'Reddit',       cat: 'social',   ring: 2, a: 4.08,   ph: 2, to: [] },
  { id: 'company',     label: 'Company',      cat: 'identity', ring: 2, a: 2.67,   ph: 2, to: [] },
  // Ring 3 — deep intelligence
  { id: 'location',    label: 'Location',     cat: 'infra',    ring: 3, a: -2.04,  ph: 3, to: [] },
  { id: 'credentials', label: 'Credentials',  cat: 'breach',   ring: 3, a: -0.63,  ph: 3, to: [] },
  { id: 'darkweb',     label: 'Dark Web',     cat: 'breach',   ring: 3, a: 0.16,   ph: 3, to: [] },
  { id: 'shodan',      label: 'Shodan',       cat: 'infra',    ring: 3, a: 0.94,   ph: 3, to: [] },
  { id: 'ip',          label: 'IP / ASN',     cat: 'infra',    ring: 3, a: 1.72,   ph: 3, to: [] },
]

// When does each discovery phase start (seconds into the 22s loop)
const PHASE_START = [0, 1.5, 5.5, 11.0]

type NodeDef = typeof DEFS[number] & { x: number; y: number; r: number }
type Particle = { from: string; to: string; t: number; sp: number }

// ── helpers ──────────────────────────────────────────────────────────
const hex2rgb = (h: string) =>
  [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)] as const

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

// ─────────────────────────────────────────────────────────────────────
export default function LinkAnalysisAnimation() {
  const cvs  = useRef<HTMLCanvasElement>(null)
  const refs = useRef<{ nodes: NodeDef[]; particles: Particle[]; t0: number }>({
    nodes: [], particles: [], t0: 0,
  })
  const raf = useRef<number>()

  useEffect(() => {
    const canvas = cvs.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!

    const RING_FRAC = [0, 0.30, 0.59, 0.88]
    const RING_R    = [20, 12, 9, 7]
    const LOOP      = 22  // seconds per full cycle

    const build = () => {
      const W = canvas.width; const H = canvas.height
      const cx = W / 2; const cy = H / 2
      const unit = Math.min(W, H) * 0.43
      refs.current.nodes = DEFS.map(d => ({
        ...d,
        x: cx + unit * RING_FRAC[d.ring] * Math.cos(d.a),
        y: cy + unit * RING_FRAC[d.ring] * Math.sin(d.a),
        r: RING_R[d.ring],
      }))
    }

    const resize = () => {
      canvas.width  = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      build()
    }
    resize()
    window.addEventListener('resize', resize)
    refs.current.t0 = performance.now()

    const draw = (now: number) => {
      const t  = ((now - refs.current.t0) / 1000) % LOOP
      const { nodes, particles } = refs.current
      const W = canvas.width; const H = canvas.height
      const cx = W / 2; const cy = H / 2
      const unit = Math.min(W, H) * 0.43

      ctx.clearRect(0, 0, W, H)

      // ── Background centre glow ──────────────────────────────────────
      const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, unit)
      bg.addColorStop(0,   'rgba(34,197,94,0.07)')
      bg.addColorStop(0.5, 'rgba(34,197,94,0.02)')
      bg.addColorStop(1,   'rgba(0,0,0,0)')
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, W, H)

      // ── Dashed concentric guide rings ───────────────────────────────
      ctx.setLineDash([3, 9])
      ctx.lineWidth = 1
      ;[1, 2, 3].forEach(i => {
        ctx.beginPath()
        ctx.arc(cx, cy, unit * RING_FRAC[i], 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(34,197,94,0.10)'
        ctx.stroke()
      })
      ctx.setLineDash([])

      // ── Rotating radar sweep ────────────────────────────────────────
      const radarA = (t / 4.5) * Math.PI * 2  // one full rotation per 4.5 s
      const radarMax = unit * RING_FRAC[3] + 10
      const SWEEP = Math.PI / 5.5

      ctx.save()
      // Filled wedge
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, radarMax, radarA - SWEEP, radarA)
      ctx.closePath()
      const sweepGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radarMax)
      sweepGrad.addColorStop(0,   'rgba(34,197,94,0)')
      sweepGrad.addColorStop(0.6, 'rgba(34,197,94,0.02)')
      sweepGrad.addColorStop(1,   'rgba(34,197,94,0.09)')
      ctx.fillStyle = sweepGrad
      ctx.fill()
      // Leading arm
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + radarMax * Math.cos(radarA), cy + radarMax * Math.sin(radarA))
      ctx.strokeStyle = 'rgba(34,197,94,0.45)'
      ctx.lineWidth = 1.5
      ctx.stroke()
      ctx.restore()

      // ── Compute appearance (resets every loop) ──────────────────────
      const getAppear = (ph: number) => {
        const elapsed = t - PHASE_START[ph]
        return clamp01(elapsed * 2.8)  // ~0.36 s to fully appear
      }
      const nMap = new Map(nodes.map(n => [n.id, n]))

      // ── Connection edges ────────────────────────────────────────────
      nodes.forEach(src => {
        const srcAl = getAppear(src.ph)
        if (srcAl < 0.05) return
        src.to.forEach(tid => {
          const dst = nMap.get(tid)
          if (!dst) return
          const dstAl = getAppear(dst.ph)
          if (dstAl < 0.05) return
          const [r, g, b] = hex2rgb(CAT[src.cat])
          ctx.beginPath()
          ctx.moveTo(src.x, src.y)
          ctx.lineTo(dst.x, dst.y)
          ctx.strokeStyle = `rgba(${r},${g},${b},${Math.min(srcAl, dstAl) * 0.35})`
          ctx.lineWidth = 1
          ctx.stroke()
        })
      })

      // ── Particle spawning ───────────────────────────────────────────
      const phase3Started = t >= PHASE_START[2]
      if (phase3Started && Math.random() < 0.22) {
        const srcs = nodes.filter(n => getAppear(n.ph) > 0.8 && n.to.length > 0)
        if (srcs.length) {
          const s  = srcs[Math.floor(Math.random() * srcs.length)]
          const tid = s.to[Math.floor(Math.random() * s.to.length)]
          const d  = nMap.get(tid)
          if (d && getAppear(d.ph) > 0.8) {
            particles.push({ from: s.id, to: tid, t: 0, sp: 0.007 + Math.random() * 0.007 })
          }
        }
      }
      if (particles.length > 55) particles.splice(0, particles.length - 55)

      // ── Draw & advance particles ────────────────────────────────────
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.t += p.sp
        if (p.t >= 1) { particles.splice(i, 1); continue }
        const s = nMap.get(p.from)!; const d = nMap.get(p.to)!
        const px = s.x + (d.x - s.x) * p.t
        const py = s.y + (d.y - s.y) * p.t
        const [r, g, b] = hex2rgb(CAT[s.cat])
        // Glow halo
        ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI*2)
        ctx.fillStyle = `rgba(${r},${g},${b},0.18)`; ctx.fill()
        // Core dot
        ctx.beginPath(); ctx.arc(px, py, 2.2, 0, Math.PI*2)
        ctx.fillStyle = `rgba(${r},${g},${b},0.95)`; ctx.fill()
      }

      // ── Draw nodes ─────────────────────────────────────────────────
      nodes.forEach(n => {
        const appear = getAppear(n.ph)
        if (appear < 0.01) return
        const radius = n.r * appear
        const [r, g, b] = hex2rgb(CAT[n.cat])
        const elapsed = t - PHASE_START[n.ph]

        ctx.save()
        ctx.globalAlpha = appear

        // Discovery ripple (plays once on reveal)
        if (elapsed >= 0 && elapsed < 0.9 && n.ph > 0) {
          const rippleR = n.r * (1 + elapsed * 5)
          const rippleA = (1 - elapsed / 0.9) * 0.7
          ctx.beginPath(); ctx.arc(n.x, n.y, rippleR, 0, Math.PI*2)
          ctx.strokeStyle = `rgba(${r},${g},${b},${rippleA})`
          ctx.lineWidth = 2; ctx.stroke()
        }

        // Outer glow
        const glowR = n.ring === 0
          ? radius * (1.7 + 0.4 * Math.sin(t * 2.5))
          : radius * 1.5
        const glow = ctx.createRadialGradient(n.x, n.y, radius * 0.4, n.x, n.y, glowR * 2.2)
        glow.addColorStop(0, `rgba(${r},${g},${b},0.40)`)
        glow.addColorStop(1, `rgba(${r},${g},${b},0)`)
        ctx.beginPath(); ctx.arc(n.x, n.y, glowR * 2.2, 0, Math.PI*2)
        ctx.fillStyle = glow; ctx.fill()

        // Filled circle
        const fill = ctx.createRadialGradient(
          n.x - radius * 0.3, n.y - radius * 0.3, 0,
          n.x, n.y, radius
        )
        fill.addColorStop(0, `rgba(${r},${g},${b},0.95)`)
        fill.addColorStop(1, `rgba(${r},${g},${b},0.40)`)
        ctx.beginPath(); ctx.arc(n.x, n.y, radius, 0, Math.PI*2)
        ctx.fillStyle = fill; ctx.fill()
        ctx.strokeStyle = `rgba(${r},${g},${b},0.85)`
        ctx.lineWidth = 1.5; ctx.stroke()

        // Label
        if (appear > 0.5) {
          const isCentre = n.ring === 0
          ctx.font = `${isCentre ? 'bold ' : ''}${isCentre ? 10 : 7.5}px monospace`
          ctx.textAlign = 'center'
          ctx.textBaseline = isCentre ? 'middle' : 'top'
          ctx.fillStyle = isCentre
            ? 'rgba(255,255,255,0.95)'
            : `rgba(210,210,210,${appear * 0.88})`
          ctx.fillText(n.label, n.x, isCentre ? n.y : n.y + radius + 3)
        }

        ctx.restore()
      })

      raf.current = requestAnimationFrame(draw)
    }

    raf.current = requestAnimationFrame(draw)
    return () => {
      window.removeEventListener('resize', resize)
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [])

  return (
    <canvas
      ref={cvs}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  )
}
